import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Box from '@material-ui/core/Box';
import { Button, CardHeader, Typography } from '@material-ui/core';
import { selectLoginError } from '../selectors';
import Form from '../../form/components/Form';
import { loginSchema, otpSchema } from '../schema';
import { requestOTP, loginWithOtp } from '../actions';
import TextInput from '../../form/components/TextInput';
import ErrorMessage from '../../loading/components/ErrorMessage';
import SubmitButton from '../../form/components/SubmitButton';
import FormPrefiller from '../../form/components/FormPrefiller';
import CountdownTimer from './CountdownTimer';
import { clearLoginError } from '../authSlice';

const OTP_LIFETIME = 5 * 60 * 1000; // same as config auth.otp_lifetime_minutes in APIv3

const LoginForm = () => {
    const [step, setStep] = useState('login_otprequest');
    // kept here explicitly: the OTP step form only holds the code
    const [credentials, setCredentials] = useState(null);
    const [username, setUsername] = useState('');
    const [otpSent, setOtpSent] = useState(true);
    const [info, setInfo] = useState(null);
    const [timerKey, setTimerKey] = useState(0);
    const [resending, setResending] = useState(false);
    const errors = useSelector(selectLoginError);
    const dispatch = useDispatch();
    const submitButton = React.useRef(null);

    const handleOtpRequestSubmit = (values) => {
        setInfo(null);
        return dispatch(requestOTP(values)).then((response) => {
            if (response.success) {
                setCredentials({ username: values.username, password: values.password });
                setUsername(values.username);
                setOtpSent(response.otpSent);
                setTimerKey((key) => key + 1);
                setStep('otp');
            }
        });
    };

    const handleOtpLoginSubmit = (values) =>
        dispatch(loginWithOtp({ credentials: { ...credentials, otp: values.otp.trim() } }));

    const backToLogin = (message = null) => {
        dispatch(clearLoginError());
        setCredentials(null);
        setInfo(message);
        setStep('login_otprequest');
    };

    const handleResend = () => {
        setResending(true);
        setInfo(null);
        dispatch(clearLoginError());
        dispatch(requestOTP(credentials)).then((response) => {
            setResending(false);
            if (response.success) {
                setTimerKey((key) => key + 1);
                setInfo('Ein neuer Verifizierungs-Code wurde versendet.');
            }
        });
    };

    return (
        <Form
            key={step}
            initialValues={step === 'login_otprequest' ? { username, password: '' } : { otp: '' }}
            validationSchema={step === 'login_otprequest' ? loginSchema : otpSchema}
            onSubmit={step === 'login_otprequest' ? handleOtpRequestSubmit : handleOtpLoginSubmit}
            onChange={() => {
                if (errors) {
                    dispatch(clearLoginError());
                }
            }}
            withoutFeedback
        >
            <FormPrefiller
                onFill={(setFieldValue) => {
                    setFieldValue('username', 'admin');
                    setFieldValue('password', 'Test!1');
                }}
                focus={submitButton}
            />
            {step === 'login_otprequest' ? (
                <>
                    <Grid container justify="center" alignItems="center">
                        <Grid item sm={8}>
                            <Card>
                                <CardHeader title="Bitte melden Sie sich mit ihren Benutzerdaten an" />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextInput
                                                label="Benutzername"
                                                name="username"
                                                errors={errors}
                                                fullWidth
                                                noTurbo
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextInput
                                                label="Passwort"
                                                name="password"
                                                type="password"
                                                errors={errors}
                                                fullWidth
                                                noTurbo
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <CardActions>
                                    <Grid container justify="space-between" alignItems="center">
                                        <Grid item>
                                            <Box ml={2}>
                                                <ErrorMessage error={errors} />
                                                {!errors && info && <Typography>{info}</Typography>}
                                            </Box>
                                        </Grid>
                                        <Grid item>
                                            <SubmitButton innerRef={submitButton} label="Login" />
                                        </Grid>
                                    </Grid>
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            ) : (
                <>
                    <Grid container justify="center" alignItems="center">
                        <Grid item sm={8}>
                            <Card>
                                <CardHeader
                                    title={
                                        otpSent
                                            ? 'Bitte geben Sie den per E-Mail erhaltenen 6-stelligen Verifizierungs-Code ein:'
                                            : 'Dev-System: Verifizierungs-Code ist deaktiviert, beliebigen Code eingeben.'
                                    }
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <input type="hidden" name="nothing" />{' '}
                                            {/* Workaround for Chrome autofill issue */}
                                            <TextInput
                                                autocomplete="one-time-code"
                                                label="Verifizierungs-Code "
                                                name="otp"
                                                errors={errors}
                                                fullWidth
                                                noTurbo
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Box ml={2}>
                                                {otpSent && (
                                                    <CountdownTimer
                                                        key={timerKey}
                                                        initialTime={OTP_LIFETIME}
                                                        info="Verbleibende Zeit:"
                                                        onExpire={() =>
                                                            backToLogin(
                                                                'Der Verifizierungs-Code ist abgelaufen. Bitte melden Sie sich erneut an.'
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <CardActions>
                                    <Grid container direction="column" spacing={2}>
                                        <Grid item>
                                            <Box ml={2}>
                                                <ErrorMessage error={errors} />
                                                {!errors && info && <Typography>{info}</Typography>}
                                            </Box>
                                        </Grid>
                                        <Grid item container spacing={2}>
                                            <Grid item>
                                                <Button onClick={() => backToLogin()}>
                                                    Zurück
                                                </Button>
                                            </Grid>
                                            {otpSent && (
                                                <Grid item>
                                                    <Button
                                                        onClick={handleResend}
                                                        disabled={resending}
                                                    >
                                                        Code erneut senden
                                                    </Button>
                                                </Grid>
                                            )}
                                            <Grid item>
                                                <SubmitButton
                                                    innerRef={submitButton}
                                                    label="Bestätigen"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}
        </Form>
    );
};

export default LoginForm;
